import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toPng } from 'html-to-image';
import { 
  Sun, 
  Coffee, 
  Sunset, 
  Moon, 
  MapPin, 
  Download, 
  Share2, 
  Copy, 
  Check,
  ArrowLeft,
  Sparkles,
  Instagram
} from 'lucide-react';
import { API } from '../lib/api';

const SLOT_CONFIG = {
  morning: { icon: Sun, color: 'amber', label: 'Morning', bgColor: '#fef3c7', textColor: '#d97706' },
  lunch: { icon: Coffee, color: 'orange', label: 'Lunch', bgColor: '#ffedd5', textColor: '#ea580c' },
  afternoon: { icon: Sunset, color: 'rose', label: 'Afternoon', bgColor: '#ffe4e6', textColor: '#e11d48' },
  dinner: { icon: Moon, color: 'indigo', label: 'Dinner', bgColor: '#e0e7ff', textColor: '#4f46e5' }
};

const GUEST_TYPE_LABELS = {
  couple: 'Romantic Getaway',
  family: 'Family Adventure',
  luxury: 'Luxury Experience',
  budget: 'Budget-Friendly'
};

export default function SharedItineraryPage() {
  const { shareId } = useParams();
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingStory, setDownloadingStory] = useState(false);
  const cardRef = useRef(null);
  const storyRef = useRef(null);

  useEffect(() => {
    fetchItinerary();
  }, [shareId]);

  const fetchItinerary = async () => {
    try {
      const res = await axios.get(`${API}/share/${shareId}`);
      setItinerary(res.data);
    } catch (err) {
      setError('Itinerary not found');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!cardRef.current) return;
    
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#1c1917'
      });
      
      const link = document.createElement('a');
      link.download = `my-puglia-day-${shareId}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error generating image:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadStory = async () => {
    if (!storyRef.current) return;
    
    setDownloadingStory(true);
    try {
      const dataUrl = await toPng(storyRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        width: 1080,
        height: 1920
      });
      
      const link = document.createElement('a');
      link.download = `my-puglia-story-${shareId}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error generating story image:', err);
    } finally {
      setDownloadingStory(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `My Perfect Day in ${itinerary?.location || 'Puglia'}`,
          text: `Check out my day itinerary curated by OspitAI!`,
          url: window.location.href
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      handleCopyLink();
    }
  };

  const openInstagram = () => {
    // Download the story image first, then prompt user to share on Instagram
    handleDownloadStory();
    // Open Instagram (works on mobile)
    setTimeout(() => {
      window.open('instagram://story-camera', '_blank');
    }, 1000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !itinerary) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="text-2xl font-serif text-white mb-4">Itinerary Not Found</h1>
          <p className="text-stone-400 mb-6">This shared itinerary may have expired or doesn't exist.</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-teal-700 transition-colors"
          >
            <ArrowLeft size={18} />
            Plan Your Own Day
          </Link>
        </div>
      </div>
    );
  }

  const { location, guest_type, itinerary: dayPlan } = itinerary;
  const locationName = location === 'monopoli' ? 'Monopoli' : 'Fasano';

  return (
    <div className="min-h-screen bg-stone-900 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Back Link */}
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          Plan your own day
        </Link>

        {/* Shareable Card */}
        <div 
          ref={cardRef}
          className="bg-gradient-to-b from-stone-800 to-stone-900 rounded-3xl overflow-hidden shadow-2xl"
          data-testid="share-card"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={20} />
              <span className="text-sm font-medium opacity-90">OspitAI</span>
            </div>
            <h1 className="text-2xl font-serif">My Perfect Day</h1>
            <p className="text-teal-100 mt-1">
              {GUEST_TYPE_LABELS[guest_type] || 'Curated Experience'} in {locationName}
            </p>
          </div>

          {/* Itinerary */}
          <div className="p-6 space-y-4">
            {['morning', 'lunch', 'afternoon', 'dinner'].map((slot) => {
              const item = dayPlan[slot];
              if (!item) return null;
              
              const config = SLOT_CONFIG[slot];
              const Icon = config.icon;
              
              return (
                <div key={slot} className="flex gap-4">
                  <div className="w-14 text-center flex-shrink-0">
                    <div 
                      className="w-10 h-10 mx-auto rounded-full flex items-center justify-center"
                      style={{ backgroundColor: config.bgColor }}
                    >
                      <Icon size={18} style={{ color: config.textColor }} />
                    </div>
                    <p className="text-xs text-stone-500 mt-1">{item.time}</p>
                  </div>
                  <div className="flex-1 pb-4 border-b border-stone-700/50 last:border-0">
                    <p className="text-xs text-stone-500 uppercase tracking-wide">{config.label}</p>
                    <h3 className="font-semibold text-white mt-0.5">
                      {item.activity?.en || item.activity}
                    </h3>
                    <p className="text-sm text-stone-400 mt-1">
                      {item.description?.en || item.description}
                    </p>
                    {item.price && (
                      <p className="text-sm text-teal-400 font-medium mt-1">{item.price}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <div className="flex items-center justify-center gap-2 text-stone-500 text-sm">
              <MapPin size={14} />
              <span>Curated by OspitAI · Puglia, Italy</span>
            </div>
          </div>
        </div>

        {/* Instagram Story Card (Hidden, for generation only) */}
        <div 
          ref={storyRef}
          className="fixed -left-[9999px] top-0"
          style={{ 
            width: '1080px', 
            height: '1920px',
            background: 'linear-gradient(180deg, #0d9488 0%, #059669 30%, #1c1917 100%)',
            padding: '60px 50px',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
          }}
        >
          {/* Story Header */}
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <div style={{ 
              width: '80px', 
              height: '80px', 
              background: 'rgba(255,255,255,0.2)', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <Sparkles size={40} color="white" />
            </div>
            <h1 style={{ color: 'white', fontSize: '56px', fontWeight: 'bold', margin: '0 0 10px' }}>
              My Perfect Day
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '32px' }}>
              {locationName}, Puglia
            </p>
          </div>

          {/* Story Content */}
          <div style={{ 
            background: 'rgba(255,255,255,0.95)', 
            borderRadius: '40px', 
            padding: '50px',
            marginBottom: '40px'
          }}>
            {['morning', 'lunch', 'afternoon', 'dinner'].map((slot, idx) => {
              const item = dayPlan[slot];
              if (!item) return null;
              const config = SLOT_CONFIG[slot];
              
              return (
                <div key={slot} style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '30px',
                  marginBottom: idx < 3 ? '40px' : '0',
                  paddingBottom: idx < 3 ? '40px' : '0',
                  borderBottom: idx < 3 ? '2px solid #e5e5e5' : 'none'
                }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    background: config.bgColor, 
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <span style={{ fontSize: '36px' }}>
                      {slot === 'morning' ? '☀️' : slot === 'lunch' ? '🍽️' : slot === 'afternoon' ? '🌅' : '🌙'}
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      color: '#78716c', 
                      fontSize: '22px', 
                      textTransform: 'uppercase',
                      letterSpacing: '2px',
                      marginBottom: '8px'
                    }}>
                      {config.label} · {item.time}
                    </p>
                    <h3 style={{ 
                      color: '#1c1917', 
                      fontSize: '34px', 
                      fontWeight: '700',
                      marginBottom: '10px'
                    }}>
                      {item.activity?.en || item.activity}
                    </h3>
                    {item.price && (
                      <p style={{ color: '#0d9488', fontSize: '28px', fontWeight: '600' }}>
                        {item.price}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Story Footer */}
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '26px', marginBottom: '15px' }}>
              Curated by
            </p>
            <p style={{ color: 'white', fontSize: '42px', fontWeight: 'bold' }}>
              OspitAI
            </p>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '24px', marginTop: '10px' }}>
              Your Hyper-Local Concierge for Puglia
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 space-y-3">
          {/* Instagram Story Button */}
          <button
            onClick={openInstagram}
            disabled={downloadingStory}
            data-testid="instagram-story-btn"
            className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white py-4 rounded-xl font-medium flex items-center justify-center gap-3 hover:from-purple-700 hover:via-pink-700 hover:to-orange-600 transition-all disabled:opacity-50"
          >
            {downloadingStory ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Instagram size={22} />
            )}
            Share to Instagram Story
          </button>

          <div className="flex gap-3">
            <button
              onClick={handleDownloadImage}
              disabled={downloading}
              className="flex-1 bg-white text-stone-900 py-3 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-stone-100 transition-colors disabled:opacity-50"
            >
              {downloading ? (
                <div className="w-5 h-5 border-2 border-stone-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download size={18} />
              )}
              Download
            </button>
            
            <button
              onClick={handleShare}
              className="flex-1 bg-teal-600 text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-teal-700 transition-colors"
            >
              <Share2 size={18} />
              Share
            </button>
          </div>
        </div>

        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className="w-full mt-3 py-3 text-stone-400 hover:text-white text-sm flex items-center justify-center gap-2 transition-colors"
        >
          {copied ? (
            <>
              <Check size={16} className="text-emerald-400" />
              <span className="text-emerald-400">Link copied!</span>
            </>
          ) : (
            <>
              <Copy size={16} />
              Copy link
            </>
          )}
        </button>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-stone-500 text-sm mb-3">Want your own curated day?</p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-medium hover:from-teal-700 hover:to-emerald-700 transition-all"
          >
            <Sparkles size={18} />
            Plan Your Day with OspitAI
          </Link>
        </div>
      </div>
    </div>
  );
}
